/**
 * @fileoverview Observation-window options for the practice-proposal card's
 * chip row.
 *
 * The model authors one interval; the user picks the one that fits their week.
 * The offered set is DERIVED from that authored value rather than being a fixed
 * ladder, because category defaults are 3/5/5/7/7 and the model may author
 * anything in 1–90 — a ladder alone could never offer 5 on a communication
 * experiment.
 *
 * The 1–90 bound is duplicated from the backend deliberately. It is a garbage
 * filter on a snapshot value, not a wire contract: the backend stays
 * authoritative, and filtering here is what keeps the server's 400 unreachable
 * through the UI.
 *
 * @module chat/practiceWindowOptions
 */

/** The baseline ladder every card offers. */
const WINDOW_LADDER = [3, 7, 14];

const MIN_INTERVAL_DAYS = 1;
const MAX_INTERVAL_DAYS = 90;

/**
 * Whether a snapshot interval is usable as a preselected chip.
 *
 * @param {*} days
 * @returns {boolean}
 */
export function isValidWindowInterval(days) {
  return Number.isInteger(days) && days >= MIN_INTERVAL_DAYS && days <= MAX_INTERVAL_DAYS;
}

/**
 * Derive the window options offered on a proposal card.
 *
 * The ladder plus the snapshot's own interval, sorted and deduped — so the
 * model's choice is always among the offered chips, whatever it authored.
 *
 * @param {number|null|undefined} intervalDays - The snapshot's authored window
 * @returns {number[]} Ascending, deduped day counts
 */
export function deriveWindowOptions(intervalDays) {
  const merged = isValidWindowInterval(intervalDays)
    ? [...WINDOW_LADDER, intervalDays]
    : [...WINDOW_LADDER];
  return [...new Set(merged)].sort((a, b) => a - b);
}

/**
 * User-facing label for a window option.
 *
 * @param {number} days
 * @returns {string}
 */
export function formatWindowOption(days) {
  if (days === 1) return '1 day';
  if (days === 14) return '2 weeks';
  return `${days} days`;
}
